import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router";
import CasinoHeading from "../../casinoComponents/casinoHeading";
import VideoBox from "../../casinoComponents/videoBox";
import CasinoLastResult from "../../casinoComponents/casinoLastResult";
import MyBet from "../../casinoComponents/myBet";
import CasinoTableLayout from "../../casinoComponents/casinoTableLayout";
import { getCasinoGameDetails, getMatchExposure, getCasinoOpenBets } from "../../../apiservices/CasionApi";
import ViewMoreModal from "../../../components/ViewMoreModal";
import { extractCasinoGame } from "../../../utils/casinoGame";
import { formatUniqueTeenNation } from "../../../utils/casinoMyBets";
import TeenUniqueCardsOverlay from "../../casinoComponents/videoBox/overlays/TeenUniqueCardsOverlay";
import ResultTeenUnique from "../resultui/resultTeenUnique/ResultTeenUnique";
import TeenUniqueRules from "./TeenUniqueRules";
import { getCasinoRulesHtml } from "../../../data/casinoRulesHtml";
import "../../../styles/games/teenunique.css";

// VideoBox shows the (i) rules itself once "teenunique" is in the shared rules data; until then, our own panel.
const OWN_RULES = !getCasinoRulesHtml("teenunique");


export default function TeenUnique() {
  const [gameData, setGameData] = useState(null);
  const [iframeSrc, setIframeSrc] = useState("");
  const [exposures, setExposures] = useState({});
  const [myBets, setMyBets] = useState([]);
  const [subData, setSubData] = useState([]);
  const [showViewMoreModal, setShowViewMoreModal] = useState(false);
  const [rulesClicks, setRulesClicks] = useState(0);

  const { pathname } = useLocation();
  const gameName = extractCasinoGame(pathname) || "teenunique";

  useEffect(() => {
    setIframeSrc(`https://stream-s-43.uhdmovies.online/casino-stream?id=${gameName}`);

    const fetchGameData = async () => {
      try {
        const response = await getCasinoGameDetails(gameName);
        setGameData(response);
      } catch (err) {
        console.error("API Fetch Error:", err.message);
        setGameData(null);
      }
    };

    fetchGameData();
    const interval = setInterval(fetchGameData, 1500);
    return () => clearInterval(interval);
  }, [pathname, gameName]);

  useEffect(() => {
    if (gameData?.data?.data?.sub) {
      const sub = gameData.data.data.sub;
      setSubData(sub || []);
    } else {
      setSubData([]);
    }
  }, [gameData]);

  const handleBetClick = () => {
    fetchExposure();
    fetchMyBetsData();
  };

  const fetchExposure = useCallback(async () => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const userId = user?.user_id || user?.id || user?.staff_id || user?.owner_id;
      const matchId = gameData?.data?.data?.mid?.toString();

      if (userId && matchId) {
        const response = await getMatchExposure(userId, matchId);
        if (response.success && response.data) {
          const exposureMap = {};
          response.data.forEach((item) => {
            const teamName = item.team_name || item.selection || item.player_name || "";
            const exposureAmount = parseFloat(item.exposure_amount) || 0;
            if (teamName && exposureAmount !== 0) {
              exposureMap[teamName] = exposureAmount;
              exposureMap[teamName.toLowerCase()] = exposureAmount;
              exposureMap[teamName.trim()] = exposureAmount;
            }
          });
          setExposures(exposureMap);
        }
      }
    } catch (error) {
      console.error("Error fetching exposure:", error);
    }
  }, [gameData?.data?.data?.mid]);

  const fetchMyBetsData = useCallback(async () => {
    if (!gameName) return;
    try {
      const response = await getCasinoOpenBets(gameName);
      if (response?.success && Array.isArray(response.data)) {
        const formattedBets = response.data.map((bet) => ({
          // My Bets "Gametype" column (casino_bets.game_name, e.g. mogambo)
          description: bet.description ?? bet.game_name ?? bet.gameName,
          userName: bet.username || bet.userName || "-",
          matchedBet: bet.matchedBet || bet.nation || bet.selection || bet.player_name || "-",
          selection: bet.selection || bet.player_name || bet.matchedBet || "",
          player_name: bet.player_name || "",
          odds: bet.odds ?? bet.userrate ?? "0",
          stake: bet.stake ?? bet.amount ?? "0",
          placeDate: bet.placeDate ?? bet.created_at ?? "-",
          gametype: bet.gametype ?? bet.game_type ?? (bet.type === "lay" ? "Lay" : bet.type === "back" ? "Back" : "-"),
          type: bet.type || null,
          exposer: parseFloat(bet.exposer || bet.exposure_amount || "0") || 0,
        }));
        setMyBets(formattedBets);
      }
    } catch (error) {
      console.error("Error fetching my bets (open-downline):", error);
    }
  }, [gameName]);

  useEffect(() => {
    const matchId = gameData?.data?.data?.mid?.toString();
    if (matchId) {
      const t = setTimeout(() => fetchExposure(), 2000);
      const exposureInterval = setInterval(fetchExposure, 2000);
      return () => {
        clearTimeout(t);
        clearInterval(exposureInterval);
      };
    }
  }, [fetchExposure, gameData?.data?.data?.mid]);

  useEffect(() => {
    fetchMyBetsData();
    const intervalId = setInterval(fetchMyBetsData, 3000);
    return () => clearInterval(intervalId);
  }, [fetchMyBetsData]);

  const resultData = gameData?.lrs ?? gameData?.data?.data?.lrs ?? gameData?.data?.lrs ?? [];
  const roundId = gameData?.data?.data?.mid || gameData?.data?.mid || "";
  const cardString =
    gameData?.data?.data?.card ??
    gameData?.data?.card ??
    (Array.isArray(gameData?.lrs) && gameData.lrs[0]?.card ? gameData.lrs[0].card : undefined);
  const remark = gameData?.data?.data?.remark ?? gameData?.data?.remark;

  // Reference (casino/teenunique): .casino-table.teenpatti20 — header, video with the 6-card strip,
  // then .casino-detail (only once the round is loaded) holding the optional remark and Last Result.
  // Admin view has no bet table for this game.
  return (
    <CasinoTableLayout tableClass="teenpatti20" rightSide={<MyBet bets={myBets} formatNation={formatUniqueTeenNation} onViewMore={() => setShowViewMoreModal(true)} />}>
      <>
        <CasinoHeading
          name="Unique Teenpatti"
          roundId={roundId}
          placedBetCount={myBets.length}
          activeTab="game"
          onTabChange={() => {}}
        />
        <VideoBox
          iframeSrc={iframeSrc}
          timerValue={gameData?.data?.data?.lt}
          gameType={gameName}
          resultData={cardString}
          cardsOverlay={cardString ? <TeenUniqueCardsOverlay cardTokens={cardString} /> : null}
          onRulesClick={OWN_RULES ? () => setRulesClicks((n) => n + 1) : undefined}
        />
        {OWN_RULES && <TeenUniqueRules clickSeq={rulesClicks} />}

        {roundId ? (
          <div className="casino-detail">
            {remark ? (
              <div className="casino-remark mt-3">
                <div className="remark-icon">
                  <img src={`${import.meta.env.BASE_URL}assets/img/icons/remark.png`} />
                </div>{" "}
                <marquee>{remark}</marquee>
              </div>
            ) : null}
            <CasinoLastResult
              gameType="teenunique"
              data={resultData}
              gameData={gameData}
              resultComponent={<ResultTeenUnique gameType="teenunique" data={resultData} gameData={gameData} />}
            />
          </div>
        ) : null}
      </>
      <ViewMoreModal
        show={showViewMoreModal}
        onHide={() => setShowViewMoreModal(false)}
        title="View More"
        sectionKey={gameName}
        formatNation={formatUniqueTeenNation}
      />
    </CasinoTableLayout>
  );
}
